import type { Ticket } from "./types";

type DeliveryStatus = NonNullable<Ticket["messages"]>[number]["deliveryStatus"];
export function reconcileDeliveryStatus(current: DeliveryStatus, incoming: DeliveryStatus): DeliveryStatus {
  if (current === "delivered") return current;
  if (current === "sent" && incoming !== "delivered") return current;
  return incoming;
}

export function updateTicketDelivery(current: Ticket | null, messageId: string, status: DeliveryStatus): Ticket | null {
  if (!current) return current;
  return { ...current, messages: current.messages?.map((message) => message.id === messageId
    ? { ...message, deliveryStatus: reconcileDeliveryStatus(message.deliveryStatus, status) } : message) };
}

export function mergeLatestTicket(current: Ticket | null, latest: Ticket): Ticket {
  if (!current || current.id !== latest.id) return latest;
  if (current.version !== undefined && latest.version !== undefined && current.version > latest.version) return current;
  const known = new Map(current.messages?.map((message) => [message.id, message.deliveryStatus]));
  const deliveryChanged = latest.messages?.some((message) => reconcileDeliveryStatus(known.get(message.id), message.deliveryStatus) !== message.deliveryStatus);
  if (deliveryChanged) latest = { ...latest, messages: latest.messages?.map((message) => ({
    ...message, deliveryStatus: reconcileDeliveryStatus(known.get(message.id), message.deliveryStatus),
  })) };
  const latestIds = new Set((latest.messages ?? []).map((message) => message.id));
  // If more than a page arrived offline, retain the latest cursor rather than
  // joining disjoint pages and making the missing middle impossible to load.
  if (latest.messagePage?.hasMore && !(current.messages ?? []).some((message) => latestIds.has(message.id))) return latest;
  if (!latest.messagePage?.hasMore) return latest;
  const previouslyLoaded = (current.messages ?? []).filter((message) => !latestIds.has(message.id));
  return {
    ...latest,
    messages: [...previouslyLoaded, ...(latest.messages ?? [])],
    messagePage: previouslyLoaded.length ? current.messagePage : latest.messagePage,
  };
}
